import { SYNC_KEY_HARDWARE_GPS_TRACKERS } from "../../../constantes";
import {
  GpsTrackerPlataformaItem,
  GpsTrackersPlataformaService,
} from "../../../shared/generated/grpc/plataforma_hardware_gps_trackers_pb";
import { GpsTrackerDiaEnviosResponse } from "../../../shared/response/hardware/GpsTrackerDiaEnviosResponse";
import { GpsTrackerEnviosResponse } from "../../../shared/response/hardware/GpsTrackerEnviosResponse";
import { GpsTrackerResponse } from "../../../shared/response/hardware/GpsTrackerResponse";
import { GpsTrackerStatsResponse } from "../../../shared/response/hardware/GpsTrackerStatsResponse";
import { GpsTrackerAsignarPayload } from "../../../shared/services/api/payload/hardware/GpsTrackerAsignarPayload";
import { ServiceGetListBase } from "../../../shared/services/base/ServiceGetListBase";
import { grpcCall, grpcClient } from "../../../shared/services/grpc/grpcCall";

/**
 * GPS trackers (`GpsTrackersPlataformaService`, gRPC): listado en memoria + stats + asignar.
 * stats no viaja en el list: llamarlo manualmente tras cada save/asignar/delete.
 */
export class GpsTrackerService extends ServiceGetListBase<GpsTrackerResponse> {
  static readonly instance = new GpsTrackerService();
  private constructor() { super(); }

  /** Clave única del listado: deduplica `sync()` (dos pedidos simultáneos, un solo request). */
  protected readonly syncKey = SYNC_KEY_HARDWARE_GPS_TRACKERS;

  /** gRPC-Web (`ListGpsTrackers`): no eliminados, con el cliente asignado. */
  protected async fetchItems(): Promise<GpsTrackerResponse[]> {
    const r = await grpcCall(() => grpcClient(GpsTrackersPlataformaService).listGpsTrackers({}));
    return r.items.map(toGpsTrackerResponse);
  }

  /** Totales para los KPIs. */
  async getStats(): Promise<GpsTrackerStatsResponse> {
    const s = await grpcCall(() => grpcClient(GpsTrackersPlataformaService).getStats({}));
    return { total: s.total, asignadosACliente: s.asignadosACliente, enOficina: s.enOficina, noPasanTest: s.noPasanTest };
  }

  /**
   * Envíos que llegaron de un GPS en un día de Colombia (`fecha` en `aaaa-mm-dd`), tal cual los mandó el aparato y en
   * orden de llegada (`DescargarEnvios`, 2026-10-29). Con `contenido` vacío no llegó nada ese día. Si el GPS no existe o
   * nunca envió datos llega `NotFoundError` con el texto del Server. No toca el listado en memoria.
   */
  async descargarEnvios(codigoInterno: number, fecha: string): Promise<GpsTrackerEnviosResponse> {
    const r = await grpcCall(() => grpcClient(GpsTrackersPlataformaService).descargarEnvios({ codigoInterno, fecha }));
    return { contenido: r.contenido, envios: r.envios };
  }

  /**
   * Días de Colombia (`aaaa-mm-dd`) en que llegaron envíos de un GPS, del más reciente al más viejo, con cuántos llegaron
   * cada día (`ListarDiasEnvios`, 2026-10-29): la ventana de descarga muestra solo esos para escoger uno. Lista vacía si
   * su base existe pero no tiene envíos. Si el GPS no existe o nunca envió datos llega `NotFoundError` con el texto del
   * Server. No toca el listado en memoria.
   */
  async listarDiasEnvios(codigoInterno: number): Promise<GpsTrackerDiaEnviosResponse[]> {
    const r = await grpcCall(() => grpcClient(GpsTrackersPlataformaService).listarDiasEnvios({ codigoInterno }));
    return r.dias.map((d) => ({ fecha: d.fecha, envios: d.envios }));
  }

  /** Asigna a un cliente (o devuelve a oficina con `clienteId` null) y reemplaza la fila en memoria. */
  async asignar(id: string, payload: GpsTrackerAsignarPayload): Promise<GpsTrackerResponse> {
    const r = await grpcCall(() => grpcClient(GpsTrackersPlataformaService).asignar({
      id,
      clienteId: payload.clienteId ?? undefined,
    }));
    const item = toGpsTrackerResponse(r);
    this.upsert(item);
    return item;
  }
}

/** Mensaje gRPC del tracker → `GpsTrackerResponse`. Lo reusa el guardado. */
export function toGpsTrackerResponse(t: GpsTrackerPlataformaItem): GpsTrackerResponse {
  return {
    id: t.id,
    isEliminado: false,
    codigoInterno: t.codigoInterno,
    codigoCliente: t.codigoCliente,
    fechaTesteo: t.fechaTesteo !== undefined ? new Date(Number(t.fechaTesteo)).toISOString() : null,
    pasaTest: t.pasaTest,
    notaTest: t.notaTest ?? null,
    clienteId: t.clienteId ?? null,
    clienteNombre: t.clienteNombre ?? null,
  };
}
